import { useEffect, useState } from "react";
import { Download, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadFile } from "@/lib/pdf";

const STATUS = { new: ["Nowy", "bg-brand-navy text-white dark:bg-white dark:text-slate-900"], in_progress: ["W trakcie", "bg-brand-yellow/20 text-yellow-800 dark:text-yellow-300"], done: ["Załatwiony", "bg-brand-teal/15 text-brand-teal"] };

export function UploadsAdmin() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/uploads").then((r) => setItems(r.data)).catch(() => toast.error("Nie udało się pobrać przesłanych dokumentów."));
  useEffect(() => { load(); }, []);
  if (!items) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;

  const setStatus = async (id, status) => {
    await api.patch(`/admin/uploads/${id}/status`, { status });
    setItems(items.map((i) => (i.id === id ? { ...i, status } : i)));
  };
  const remove = async (id) => {
    if (!window.confirm("Usunąć ten dokument z listy?")) return;
    await api.delete(`/admin/uploads/${id}`);
    setItems(items.filter((i) => i.id !== id));
    toast.success("Usunięto.");
  };
  const download = async (it) => {
    setBusy(it.id);
    try {
      await downloadFile({ method: "get", url: `/admin/uploads/${it.id}/file` }, it.filename || "dokument.pdf");
    } catch {
      toast.error("Nie udało się pobrać pliku.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="uploads-admin" className="space-y-4">
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6">
        <h2 className="font-display font-bold text-lg">Przesłane podpisane dokumenty</h2>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          Pliki PDF wysłane przez klientów przez skrzynkę „Wyślij podpisany dokument” (strona /biuro-wirtualne-warszawa/dokumenty i generator). Każdy plik przeszedł weryfikację obecności podpisu elektronicznego;
          dane podpisującego pochodzą z certyfikatu. To nie jest kryptograficzna weryfikacja integralności — w razie wątpliwości sprawdź plik w usłudze gov.pl.
        </p>
      </div>
      {items.length === 0 && <p data-testid="uploads-empty" className="text-sm text-muted-foreground italic px-2">Brak przesłanych dokumentów.</p>}
      {items.map((it) => {
        const [label, cls] = STATUS[it.status] || STATUS.new;
        return (
          <div key={it.id} data-testid={`upload-row-${it.id}`} className="bg-card border border-border rounded-2xl p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                  <span className="font-semibold">{it.company || it.email}</span>
                  {it.doc_name && <span className="text-xs text-muted-foreground">· {it.doc_name}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{it.created_at?.slice(0, 16).replace("T", " ")} · {it.email}{it.nip && ` · NIP ${it.nip}`} · {it.filename} ({Math.round(it.size / 1024)} KB)</p>
                <ul className="mt-2 space-y-0.5">
                  {(it.signatures || []).map((s, i) => (
                    <li key={i} className="text-sm inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-brand-teal" /> {s.name || "podpis elektroniczny"}{s.issuer && <span className="text-muted-foreground">· {s.issuer}</span>}{s.time && <span className="text-muted-foreground">· {s.time}</span>}</li>
                  ))}
                </ul>
                {it.note && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{it.note}</p>}
              </div>
              <div className="flex items-center gap-2">
                <select data-testid={`upload-status-${it.id}`} value={it.status} onChange={(e) => setStatus(it.id, e.target.value)} className="h-9 rounded-full border border-border bg-background px-3 text-xs font-semibold">
                  {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <button type="button" data-testid={`upload-download-${it.id}`} onClick={() => download(it)} disabled={busy === it.id} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors disabled:opacity-60">
                  {busy === it.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Pobierz PDF
                </button>
                <button type="button" data-testid={`upload-delete-${it.id}`} onClick={() => remove(it.id)} aria-label="Usuń" className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
